import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { subscriptionAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, XCircle, ArrowRight, Crown } from 'lucide-react';
import { useTranslation } from '../lib/i18n';

export default function SubscriptionSuccessPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentData, setPaymentData] = useState(null);
  const hasPolled = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (hasPolled.current) return;
    hasPolled.current = true;

    const sessionId = searchParams.get('session_id');

    if (!sessionId) {
      setStatus('error');
      toast.error(t('subSuccess.invalidSession') || 'Ungültige Sitzung.');
      return;
    }

    pollPaymentStatus(sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      toast.error(
        t('subSuccess.timeoutToast')
        || 'Zahlungsbestätigung dauert ungewöhnlich lange. Bitte E-Mail prüfen.'
      );
      return;
    }

    try {
      const response = await subscriptionAPI.getCheckoutStatus(sessionId);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(data);
        toast.success(t('subSuccess.toast') || 'Zahlung erfolgreich. Willkommen!');
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        toast.error(t('subSuccess.expired') || 'Zahlungs-Sitzung ist abgelaufen.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      // Continue polling on error
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f7f3ea]">
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1f4d3a]/10 via-transparent to-transparent" />
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#dfe8db] rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-md mx-4 glass p-8 rounded-2xl text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-[#1f4d3a] animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-[#23211a] mb-2">
              {t('subSuccess.verifyingTitle') || 'Zahlung wird bestätigt…'}
            </h1>
            <p className="text-[#6f6a5c]">
              {t('subSuccess.verifyingBody') || 'Einen Moment, wir aktivieren dein Abo.'}
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-[#1e7a4f]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-12 h-12 text-green-500" />
            </div>
            <div className="flex items-center justify-center gap-2 mb-4">
              <Crown className="w-8 h-8 text-[#8f6f3a]" />
            </div>
            <h1 className="text-2xl font-bold text-[#23211a] mb-2">
              {t('subSuccess.welcomeTitle') || 'Willkommen!'}
            </h1>
            <p className="text-[#6f6a5c] mb-6">
              {t('subSuccess.welcomeBodyWizard')
                || 'Dein Abo ist aktiv. Lass uns die nächsten 7 Schritte durchgehen. Profil, Datenquelle, Templates, Voiceover, Social Media und Auto-Post. Danach läuft alles automatisch.'}
            </p>
            <Button
              data-testid="go-to-setup-button"
              className="bg-[#1f4d3a] hover:bg-[#163a2c] text-white w-full mb-2"
              onClick={() => navigate('/setup?fresh=1')}
            >
              {t('subSuccess.startSetup') || 'Setup starten'} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              data-testid="go-to-dashboard-button"
              className="text-[#6f6a5c] hover:bg-[#efe9db] w-full"
              onClick={() => navigate('/dashboard')}
            >
              {t('subSuccess.skipToDashboard') || 'Später, zum Dashboard'}
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 bg-[#f3ddd5] rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-[#b3543f]" />
            </div>
            <h1 className="text-2xl font-bold text-[#23211a] mb-2">
              {t('subSuccess.errorTitle') || 'Zahlung konnte nicht bestätigt werden'}
            </h1>
            <p className="text-[#6f6a5c] mb-8">
              {t('subSuccess.errorBody')
                || 'Wenn dir Geld abgebucht wurde, melde dich bitte unter support@real-content.at. Wir kümmern uns sofort.'}
            </p>
            <div className="space-y-3">
              <Button
                variant="outline"
                className="w-full border-[#d8d0bd] text-[#4a463b]"
                onClick={() => navigate('/pricing')}
              >
                {t('subSuccess.tryAgain') || 'Erneut versuchen'}
              </Button>
              <Button
                className="bg-[#1f4d3a] hover:bg-[#163a2c] text-white w-full"
                onClick={() => navigate('/dashboard')}
              >
                {t('subSuccess.goDashboard') || 'Zum Dashboard'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
